import React from 'react';
import { BsBoxSeam } from 'react-icons/bs';
import { MdPhotoCamera } from 'react-icons/md';
import { C, CARD, H2, SUB, BTN_PRIMARY, BTN_SECONDARY, BTN_DANGER_LINK, INPUT, INPUT_ERR, INPUT_RO, btn, Notice, Field, TextInput, TaskRow, BUSY } from './ui';
import { LIMITS, UNITS_OF_MEASURE, VERIFY_DELAYS_MS, companyName, plOf, vendorOf, isItemCodeFormatOk } from './wizardLogic';

const SECTION = { fontSize: '0.8rem', fontWeight: 800, color: C.text, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '1.25rem 0 0.5rem' };
const digitsOnly = (v) => v.replace(/\D/g, '');

const CHECK_VIEW = {
    checking: { color: C.info, icon: '…' },
    available: { color: C.ok, icon: '✓' },
    taken: { color: '#DC2626', icon: '✕' },
    unverified: { color: C.warn, icon: '!' },
};

// Línea de disponibilidad bajo el campo (role=status para lectores de pantalla).
function CheckLine({ check, fallback }) {
    const v = CHECK_VIEW[check.status];
    if (!v) return fallback || null;
    return (
        <span role="status" style={{ color: v.color, fontWeight: check.status === 'taken' ? 700 : 600, display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
            {check.status === 'checking'
                ? <><span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} aria-hidden="true"></span> Checking availability…</>
                : <><span aria-hidden="true">{v.icon}</span> {check.message}</>}
        </span>
    );
}

const MAX_OPTIONS = 50;
const OPTION_BASE = { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', minHeight: '44px', fontSize: '0.85rem', borderBottom: `1px solid ${C.line}` };

// Item code con autocompletado: mientras se escribe, lista los items de la
// empresa cuyo código empieza igual (guía para seguir la sucesión, no
// seleccionables). Si el código no existe, la opción es "Item code available"
// (con el chequeo en vivo de portal + Sage). Arriba, el siguiente código libre
// de esa sucesión (PK1002 → PK1003).
class ItemCodeCombo extends React.Component {
    state = { open: false, active: -1 };

    componentWillUnmount() { clearTimeout(this._blurTimer); }

    options() {
        const { value, search, suggestion, codeCheck } = this.props;
        const typed = String(value || '').toUpperCase();
        const rows = search.rows || [];
        // La búsqueda es por prefijo en el servidor; filtrar aquí también evita
        // mostrar resultados viejos mientras llega la nueva respuesta.
        const matches = rows.filter((r) => r.itemCode.toUpperCase().indexOf(typed) === 0);
        const exact = rows.some((r) => r.itemCode.toUpperCase() === typed);
        const opts = [];
        if (suggestion && suggestion.code !== typed && (!typed || suggestion.code.indexOf(typed) === 0)) {
            opts.push({ kind: 'suggestion', code: suggestion.code, after: suggestion.after });
        }
        // "Available" solo cuando ya respondió la búsqueda de este texto y
        // ningún código existente empieza así (PK1008 → única opción).
        const searchDone = typed.length >= this.props.minChars && search.query === typed && !search.loading;
        if (typed && !exact && isItemCodeFormatOk(typed) && searchDone && matches.length === 0) {
            opts.push({ kind: 'available', code: typed, check: codeCheck.value === typed ? codeCheck : { status: 'checking' } });
        }
        matches.slice(0, MAX_OPTIONS).forEach((r) => opts.push({ kind: 'existing', code: r.itemCode, row: r, exact: r.itemCode.toUpperCase() === typed }));
        return { opts, total: Math.max(search.total || 0, matches.length) };
    }

    selectable = (o) => o.kind === 'suggestion' || (o.kind === 'available' && o.check.status !== 'taken');

    choose(o) {
        if (o.kind === 'suggestion') this.props.onChange(o.code);
        this.setState({ open: false, active: -1 });
    }

    onKeyDown = (e) => {
        const { opts } = this.options();
        const idx = opts.map((o, i) => (this.selectable(o) ? i : -1)).filter((i) => i >= 0);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!idx.length) { this.setState({ open: true }); return; }
            const pos = idx.indexOf(this.state.active);
            const next = e.key === 'ArrowDown' ? idx[(pos + 1) % idx.length] : idx[(pos - 1 + idx.length) % idx.length];
            this.setState({ open: true, active: next });
        } else if (e.key === 'Enter' && this.state.open && this.state.active >= 0 && opts[this.state.active]) {
            e.preventDefault();
            this.choose(opts[this.state.active]);
        } else if (e.key === 'Escape') {
            this.setState({ open: false, active: -1 });
        }
    };

    renderOption(o, i) {
        const { id } = this.props;
        const active = i === this.state.active;
        const common = { id: `${id}-opt-${i}`, role: 'option', 'aria-selected': active, key: `${o.kind}-${o.code}` };
        const hover = active ? { background: '#F1F5F9' } : {};
        if (o.kind === 'suggestion') {
            return (
                <li {...common} onMouseDown={(e) => e.preventDefault()} onClick={() => this.choose(o)} style={{ ...OPTION_BASE, background: C.infoBg, cursor: 'pointer', ...(active ? { background: '#DBEAFE' } : {}) }}>
                    <span aria-hidden="true" style={{ color: C.primary, fontWeight: 800 }}>→</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 800, color: C.primaryDark }}>{o.code}</span>
                    <span style={{ color: C.info, fontSize: '0.78rem' }}>Suggested next code{o.after ? ` (after ${o.after})` : ''}</span>
                </li>
            );
        }
        if (o.kind === 'available') {
            const st = o.check.status;
            const view = st === 'taken'
                ? { color: '#DC2626', icon: '✕', text: o.check.message }
                : st === 'unverified'
                    ? { color: C.warn, icon: '!', text: `Not in this product line — ${o.check.message}` }
                    : st === 'available'
                        ? { color: C.ok, icon: '✓', text: 'Item code available' }
                        : { color: C.info, icon: null, text: 'Not in this product line — checking Sage…' };
            return (
                <li {...common} aria-disabled={st === 'taken'} onMouseDown={(e) => e.preventDefault()} onClick={() => this.choose(o)} style={{ ...OPTION_BASE, cursor: st === 'taken' ? 'default' : 'pointer', background: st === 'taken' ? C.errBg : st === 'available' ? C.okBg : '#FFFFFF', ...(active && st !== 'taken' ? { filter: 'brightness(0.97)' } : {}) }}>
                    {view.icon
                        ? <span aria-hidden="true" style={{ color: view.color, fontWeight: 800 }}>{view.icon}</span>
                        : <span className="spinner-border spinner-border-sm" style={{ width: '0.75rem', height: '0.75rem', color: view.color }} aria-hidden="true"></span>}
                    <span style={{ fontFamily: 'monospace', fontWeight: 800, color: C.ink }}>{o.code}</span>
                    <span style={{ color: view.color, fontWeight: 600, fontSize: '0.8rem', minWidth: 0 }}>{view.text}</span>
                </li>
            );
        }
        return (
            <li {...common} aria-disabled="true" onMouseDown={(e) => e.preventDefault()} style={{ ...OPTION_BASE, cursor: 'default', background: o.exact ? C.errBg : '#FFFFFF', ...hover }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: o.exact ? C.err : C.ink, flexShrink: 0 }}>{o.code}</span>
                <span style={{ color: C.faint, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{o.row.description}</span>
                {o.row.productLine && <span style={{ fontSize: '0.68rem', color: C.faint, fontWeight: 700, flexShrink: 0, border: `1px solid ${C.line}`, borderRadius: '4px', padding: '0 4px' }}>{o.row.productLine}</span>}
                {o.row.inactive && <span style={{ fontSize: '0.66rem', color: C.warn, fontWeight: 700, flexShrink: 0 }}>INACTIVE</span>}
                {o.exact && <span style={{ fontSize: '0.7rem', color: C.err, fontWeight: 800, flexShrink: 0 }}>EXISTS</span>}
            </li>
        );
    }

    render() {
        const { id, value, onChange, error, readOnly, search, minChars, companyLabel, onRetry } = this.props;
        const { open } = this.state;
        const { opts, total } = this.options();
        const listId = `${id}-list`;
        const showList = open && !readOnly;
        const typed = String(value || '').trim();
        const tooShort = typed.length < minChars;
        const searching = search.loading && !tooShort;
        return (
            <div style={{ position: 'relative' }}>
                <input
                    id={id}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showList}
                    aria-controls={listId}
                    aria-activedescendant={showList && this.state.active >= 0 ? `${id}-opt-${this.state.active}` : undefined}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={error ? `${id}-err` : undefined}
                    autoComplete="off"
                    value={value || ''}
                    readOnly={readOnly}
                    maxLength={LIMITS.itemCode}
                    placeholder="Start typing, e.g. PK100"
                    onChange={(e) => { onChange(e.target.value.toUpperCase().replace(/\s/g, '')); this.setState({ open: true, active: -1 }); }}
                    onFocus={() => { clearTimeout(this._blurTimer); if (!readOnly) this.setState({ open: true }); }}
                    onBlur={() => { this._blurTimer = setTimeout(() => this.setState({ open: false, active: -1 }), 120); }}
                    onKeyDown={this.onKeyDown}
                    style={readOnly ? INPUT_RO : error ? INPUT_ERR : INPUT}
                />
                {showList && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30, background: '#FFFFFF', border: `1px solid ${C.lineStrong}`, borderRadius: '10px', boxShadow: '0 10px 30px rgba(15,23,42,0.15)', overflow: 'hidden' }}>
                        <div style={{ padding: '0.45rem 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: C.faint, background: '#F8FAFC', borderBottom: `1px solid ${C.line}`, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            {tooShort ? `Existing item codes · ${companyLabel}` : `Codes starting with ${typed.toUpperCase()} · ${companyLabel}`}
                        </div>
                        {tooShort && <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: C.faint }}>Type at least {minChars} characters to see the existing codes.</div>}
                        <ul id={listId} role="listbox" aria-label="Item codes" style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '280px', overflowY: 'auto' }}>
                            {opts.map((o, i) => this.renderOption(o, i))}
                        </ul>
                        {searching && <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: C.faint }}><span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} aria-hidden="true"></span> Searching…</div>}
                        {search.error && !tooShort && (
                            <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: C.warn }}>
                                {search.error}{' '}
                                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onRetry} style={{ ...BTN_SECONDARY, minHeight: '32px', padding: '0.2rem 0.6rem' }}>Retry</button>
                            </div>
                        )}
                        {!tooShort && !searching && search.loaded && !opts.length && (
                            <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: C.faint }}>No item codes start with {typed.toUpperCase()}.</div>
                        )}
                        {total > MAX_OPTIONS && <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.72rem', color: C.faint }}>Showing {MAX_OPTIONS} of {total} — keep typing to narrow down.</div>}
                        <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.68rem', color: '#94A3B8', borderTop: `1px solid ${C.line}` }}>List from the Sage mirror (recent items may be missing). The code is always confirmed against Sage.</div>
                    </div>
                )}
            </div>
        );
    }
}

const DIMENSIONS = [
    { id: 'weight', label: 'Weight', unit: 'lbs' },
    { id: 'length', label: 'Length', unit: 'in' },
    { id: 'height', label: 'Height', unit: 'in' },
    { id: 'width', label: 'Width', unit: 'in' },
];

function ProductForm({ form, errors, readOnly, onField, codeCheck, upcCheck, codeSearch, searchMinChars, companyLabel, suggestion, onRetrySearch }) {
    const text = (id, label, opts) => {
        const o = opts || {};
        return (
            <Field id={`p-${id}`} label={label} required={!o.optional} error={errors[id]} hint={o.check && !readOnly ? <CheckLine check={o.check} fallback={o.hint} /> : o.hint}>
                <TextInput
                    id={`p-${id}`} value={form[id]} error={errors[id]} readOnly={readOnly}
                    inputMode={o.inputMode} placeholder={o.placeholder} maxLength={o.max}
                    onChange={(v) => onField(id, o.transform ? o.transform(v) : v)}
                />
            </Field>
        );
    };
    return (
        <>
            <div style={SECTION}>Identity</div>
            <div className="row g-3">
                <div className="col-12 col-md-6">
                    <Field id="p-itemCode" label="Item code" required error={errors.itemCode} hint={codeCheck.status === 'taken' && !readOnly ? <CheckLine check={codeCheck} /> : 'Same code in every company'}>
                        <ItemCodeCombo
                            id="p-itemCode" value={form.itemCode} error={errors.itemCode} readOnly={readOnly}
                            search={codeSearch} minChars={searchMinChars} companyLabel={companyLabel} suggestion={suggestion} codeCheck={codeCheck}
                            onChange={(v) => onField('itemCode', v)} onRetry={onRetrySearch}
                        />
                    </Field>
                </div>
                <div className="col-12 col-md-6">{text('description', 'Description (Sage)', { max: LIMITS.itemDesc, hint: `Max ${LIMITS.itemDesc} characters` })}</div>
                <div className="col-12">{text('fullName', 'Full product name (portal)', { optional: true, max: LIMITS.fullName, hint: 'Shown in the catalog; defaults to the description' })}</div>
            </div>

            <div style={SECTION}>Barcodes</div>
            <div className="row g-3">
                <div className="col-12 col-md-6">{text('upc', 'UPC', { inputMode: 'numeric', max: 14, placeholder: '012345678901', check: upcCheck, transform: digitsOnly })}</div>
                <div className="col-12 col-md-6">{text('upc2', 'UPC 2', { optional: true, inputMode: 'numeric', max: 14, transform: digitsOnly })}</div>
                <div className="col-12 col-md-6">{text('caseUpc', 'Case UPC', { inputMode: 'numeric', max: 14, transform: digitsOnly })}</div>
                <div className="col-12 col-md-6">
                    <Field id="p-um" label="Unit of measure" required error={errors.um}>
                        <select
                            id="p-um" value={form.um || ''} disabled={readOnly}
                            aria-invalid={errors.um ? 'true' : 'false'}
                            aria-describedby={errors.um ? 'p-um-err' : undefined}
                            onChange={(e) => onField('um', e.target.value)}
                            style={readOnly ? INPUT_RO : errors.um ? INPUT_ERR : INPUT}
                        >
                            {!form.um && <option value="">Select a unit…</option>}
                            {form.um && UNITS_OF_MEASURE.indexOf(form.um) < 0 && <option value={form.um}>{form.um}</option>}
                            {UNITS_OF_MEASURE.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </Field>
                </div>
            </div>

            <div style={SECTION}>Physical dimensions</div>
            <div className="row g-3">
                {DIMENSIONS.map((d) => (
                    <div className="col-6 col-md-3" key={d.id}>
                        <Field id={`p-${d.id}`} label={`${d.label} (${d.unit})`} required error={errors[d.id]}>
                            <TextInput id={`p-${d.id}`} value={form[d.id]} error={errors[d.id]} readOnly={readOnly} inputMode="decimal" placeholder="0.00" onChange={(v) => onField(d.id, v.replace(/[^\d.]/g, ''))} />
                        </Field>
                    </div>
                ))}
            </div>
        </>
    );
}

function ImagePicker({ product, imageFile, imageError, readOnly, onImage, onRemoveImage }) {
    const inputId = 'p-image';
    const missingFile = product.imageName && !imageFile && product.imageTask.status !== 'done';
    return (
        <>
            <div style={SECTION}>Product image <span style={{ textTransform: 'none', fontWeight: 400, color: '#94A3B8' }}>(optional)</span></div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: '96px', height: '96px', borderRadius: '10px', border: `1px dashed ${C.lineStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#F8FAFC', flexShrink: 0 }}>
                    {imageFile ? <img src={imageFile.dataUrl} alt="Selected product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <MdPhotoCamera style={{ fontSize: '1.75rem', color: '#94A3B8' }} aria-hidden="true" />}
                </div>
                <div style={{ minWidth: 0 }}>
                    {!readOnly && (
                        <>
                            <label htmlFor={inputId} style={{ ...BTN_SECONDARY, cursor: 'pointer' }}>{product.imageName ? 'Change image' : 'Choose image'}</label>
                            <input id={inputId} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { onImage(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                        </>
                    )}
                    {product.imageName && <div style={{ fontSize: '0.8rem', color: C.faint, marginTop: '0.35rem', wordBreak: 'break-all' }}>{product.imageName}</div>}
                    {product.imageName && product.imageTask.status !== 'done' && (
                        <button type="button" style={BTN_DANGER_LINK} onClick={onRemoveImage}>Remove image</button>
                    )}
                    {!product.imageName && <div style={{ fontSize: '0.78rem', color: C.faint, marginTop: '0.35rem' }}>PNG, JPG or WEBP, up to 5 MB. Square works best.</div>}
                </div>
            </div>
            {imageError && <Notice kind="error">{imageError}</Notice>}
            {missingFile && <Notice kind="warn">The image wasn't kept after reloading the page. Choose it again or remove it to continue.</Notice>}
        </>
    );
}

// Paso "Item": en la principal se captura el producto (una vez para todas);
// en las adicionales solo se manda a Sage con su PL y vendor.
export default function ItemStep(props) {
    const { draft, idCompany, role, isMain, locked, busy, errors, dupError, dupWarning, imageFile, imageError, onField, onImage, onRemoveImage, onCreate, onRetry, codeSearch, searchMinChars, suggestion, codeCheck, upcCheck, onRetrySearch } = props;
    const c = draft.companies[idCompany];
    const product = draft.product;
    const pl = plOf(c);
    const vendor = vendorOf(c);
    const task = c.item.task;
    const anyBusy = busy || BUSY.indexOf(task.status) >= 0;
    // portalTask es único por producto y lo completa la principal: en una
    // adicional ya viene en "done" y no indica que su item haya empezado.
    const started = task.status !== 'idle' || (isMain && product.portalTask.status !== 'idle');
    const needsImage = isMain && product.imageName && !imageFile && product.imageTask.status !== 'done';
    const blockedByCheck = isMain && !locked && (codeCheck.status === 'taken' || upcCheck.status === 'taken' || codeCheck.status === 'checking' || upcCheck.status === 'checking');

    return (
        <div style={CARD}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <BsBoxSeam style={{ fontSize: '1.3rem', color: C.primary, flexShrink: 0 }} aria-hidden="true" />
                <div>
                    <h2 style={H2}>{isMain ? 'Product information' : 'Create item'} · {companyName(idCompany)}</h2>
                    <p style={{ ...SUB, marginTop: 0 }}>{role} company</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', background: '#F8FAFC', border: `1px solid ${C.line}`, borderRadius: '10px', padding: '0.75rem 1rem', marginTop: '1rem', fontSize: '0.85rem' }}>
                <div><span style={{ color: C.faint }}>Product line:</span> <b>{pl ? `${pl.code} — ${pl.desc}` : '—'}</b></div>
                <div><span style={{ color: C.faint }}>Vendor:</span> <b>{vendor ? `${vendor.code} — ${vendor.name}` : '—'}</b></div>
            </div>

            {isMain ? (
                <>
                    {locked && <Notice kind="info">The product was already sent to Sage, so its information can't be edited here anymore.</Notice>}
                    <ProductForm
                        form={product.form} errors={errors} readOnly={locked || anyBusy} onField={onField}
                        codeCheck={codeCheck} upcCheck={upcCheck} codeSearch={codeSearch} searchMinChars={searchMinChars}
                        companyLabel={companyName(idCompany)} suggestion={suggestion} onRetrySearch={onRetrySearch}
                    />
                    <ImagePicker product={product} imageFile={imageFile} imageError={imageError} readOnly={locked && product.imageTask.status === 'done'} onImage={onImage} onRemoveImage={onRemoveImage} />
                </>
            ) : (
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: C.text }}>
                    <div><span style={{ color: C.faint }}>Item code:</span> <b>{product.form.itemCode}</b></div>
                    <div><span style={{ color: C.faint }}>Description:</span> <b>{product.form.description}</b></div>
                    <div><span style={{ color: C.faint }}>UPC:</span> <b>{product.form.upc}</b> · <span style={{ color: C.faint }}>UM:</span> <b>{product.form.um}</b></div>
                    <p style={{ ...SUB, fontSize: '0.8rem' }}>Same product data as {companyName(draft.mainCompany)}; only the product line and vendor change.</p>
                </div>
            )}

            {dupError && <Notice kind="error">{dupError}</Notice>}
            {dupWarning && <Notice kind="warn">{dupWarning}</Notice>}

            {!started && (
                <button type="button" style={{ ...btn(BTN_PRIMARY, anyBusy || needsImage || blockedByCheck), width: '100%', marginTop: '1.25rem' }} disabled={anyBusy || needsImage || blockedByCheck} onClick={onCreate}>
                    {isMain ? 'Validate & create item in Sage' : `Create item in ${companyName(idCompany)} Sage`}
                </button>
            )}

            {started && (
                <div style={{ marginTop: '1.25rem' }}>
                    <TaskRow title={`Item in ${companyName(idCompany)} Sage`} detail={product.form.itemCode} task={task} total={VERIFY_DELAYS_MS.length} onRetry={anyBusy ? null : onRetry} />
                    {task.status === 'conflict' && (
                        <Notice kind="info">
                            {isMain ? 'Change the item code, or go back and pick the product line / vendor that Sage already has.' : 'Go back and pick the product line / vendor that Sage already has for this item.'}
                        </Notice>
                    )}
                    {isMain && (
                        <TaskRow title="Item in the portal" detail="Barcodes, dimensions, catalog record" task={product.portalTask} onRetry={anyBusy || task.status !== 'done' ? null : onRetry} />
                    )}
                    {isMain && product.imageName && (
                        <TaskRow title="Product image" detail={product.imageName} task={product.imageTask} onRetry={anyBusy || product.portalTask.status !== 'done' || needsImage ? null : onRetry} />
                    )}
                    {task.status === 'conflict' && !task.sent && (
                        <button type="button" style={{ ...btn(BTN_PRIMARY, anyBusy || needsImage), width: '100%', marginTop: '0.75rem' }} disabled={anyBusy || needsImage} onClick={onCreate}>Try again</button>
                    )}
                </div>
            )}
        </div>
    );
}
